import {
    ATTR_CREATED_AT,
    ATTR_CREATED_BY_ID,
    ATTR_DELETED_AT,
    ATTR_UPDATED_AT,
    ATTR_UPDATED_BY_ID,
    Collections,
    region
} from '#shared/constants/firebase'
import { getApp, getApps, initializeApp } from 'firebase/app'
import { connectAuthEmulator, getAuth } from 'firebase/auth'
import type {
    CollectionReference,
    DocumentData,
    DocumentSnapshot,
    Firestore,
    QueryDocumentSnapshot
} from 'firebase/firestore'
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions'
import { connectStorageEmulator, getStorage } from 'firebase/storage'

// Firestore is loaded on demand (see utils/firebase/lazyFirestore.ts) so anonymous visitors on
// public pages don't download it. These bindings only work after ensureFirestore() resolves.
import {
    addDoc,
    collection,
    connectFirestoreEmulator,
    doc,
    DocumentReference,
    getDoc,
    getDocs,
    initializeFirestore,
    limit,
    loadFirestoreSdk,
    onFirestoreUsedBeforeLoad,
    Query,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where
} from '@/utils/firebase/lazyFirestore'
import { sanitizeForFirestore } from '@/utils/firestoreSanitize'

// Create Nuxt plugin
export default defineNuxtPlugin({
    name: 'firebase',
    setup() {
        // Access runtime config
        const config = useRuntimeConfig()
        // const firebaseConfig = createFirebaseConfig(config)

        // Initialize Firebase with runtime config values
        const firebaseConfig = {
            apiKey: config.public.FIREBASE_API_KEY,
            authDomain: config.public.FIREBASE_AUTH_DOMAIN,
            databaseURL: config.public.FIREBASE_DATABASE_URL,
            projectId: config.public.FIREBASE_PROJECT_ID,
            storageBucket: config.public.FIREBASE_STORAGE_BUCKET,
            messagingSenderId: config.public.FIREBASE_MESSAGING_SENDER_ID,
            appId: config.public.FIREBASE_APP_ID,
            measurementId: config.public.FIREBASE_MEASUREMENT_ID
        }

        const isUsingEmulator = Boolean(config.public.FIREBASE_USE_EMULATOR)

        // Initialize Firebase
        const app = getApps().length ? getApp() : initializeApp(firebaseConfig)

        const fn = getFunctions(app, region)
        const auth = getAuth(app)
        const storage = getStorage(app)

        if (isUsingEmulator) {
            connectAuthEmulator(auth, 'http://localhost:9099')
            connectFunctionsEmulator(fn, 'localhost', 5001)
            connectStorageEmulator(storage, 'localhost', 9199)
        }

        // Firestore is initialised on first ensureFirestore() call, not at startup.
        let db: Firestore | undefined
        let collections: FirebaseCollections | undefined
        let firestoreReady: Promise<Firestore> | undefined

        /**
         * Loads the Firestore SDK and initialises `db` and `collections`. Safe to call repeatedly.
         * The auth flow awaits it for signed-in users; middleware/firestore.global.ts awaits it
         * before routes that need it.
         */
        function ensureFirestore(): Promise<Firestore> {
            firestoreReady ??= loadFirestoreSdk()
                .then(() => {
                    const firestore = initializeFirestore(app, {
                        experimentalForceLongPolling: isUsingEmulator,
                        ignoreUndefinedProperties: true
                    })
                    if (isUsingEmulator) {
                        connectFirestoreEmulator(firestore, 'localhost', 8080)
                    }
                    collections = createCollections(firestore)
                    db = firestore
                    return firestore
                })
                .catch((error) => {
                    firestoreReady = undefined
                    throw error
                })
            return firestoreReady
        }

        // Code that reaches Firestore before it is loaded still throws, but this starts the load
        // so a retry succeeds.
        onFirestoreUsedBeforeLoad(() => {
            ensureFirestore().catch((error) => console.error('[Firebase] Could not load Firestore', error))
        })

        function firestoreNotLoaded(name: string): Error {
            ensureFirestore().catch((error) => console.error('[Firebase] Could not load Firestore', error))
            const error = new Error(
                `[Firebase] $firebase.${name} was used before Firestore was loaded. ` +
                    'Await $firebase.ensureFirestore() first, or take the route off the anonymous allowlist in middleware/firestore.global.ts.'
            )
            // Callers often catch and swallow errors; make sure this one is seen during development.
            if (import.meta.dev) console.error(error)
            return error
        }

        function requireDb(): Firestore {
            if (!db) throw firestoreNotLoaded('db')
            return db
        }

        function requireCollections(): FirebaseCollections {
            if (!collections) throw firestoreNotLoaded('collections')
            return collections
        }

        interface Options {
            withTimestamps?: boolean
            withAuthor?: boolean
            withSoftDelete?: boolean
        }

        interface SerializedDocument extends DocumentData {
            id: string
        }

        type FirestoreCollectionInput = string | CollectionReference
        type FirestoreReadInput = FirestoreCollectionInput | Query

        class DocumentNotFound extends Error {
            constructor(collection: CollectionReference, m: string) {
                super(`Document not found ${collection.path}: ${m}`)

                Object.setPrototypeOf(this, DocumentNotFound.prototype)
            }
        }

        function getErrorMessage(error: unknown) {
            return error instanceof Error ? error.message : String(error ?? '')
        }

        function isRecoverableFirestoreReadError(error: unknown) {
            const message = getErrorMessage(error)
            return (
                message.includes('FIRESTORE (12.8.0) INTERNAL ASSERTION FAILED') ||
                message.includes('nullValue') ||
                message.includes(`right-hand side of 'in' should be an object, got null`)
            )
        }

        function emptyDocument<T>() {
            return null as T
        }

        async function withRecoverableFirestoreRead<T>(
            operation: string,
            read: () => Promise<T>,
            fallback: () => T
        ): Promise<T> {
            try {
                return await read()
            } catch (error) {
                return handleRecoverableFirestoreReadError(operation, error, fallback)
            }
        }

        function handleRecoverableFirestoreReadError<T>(operation: string, error: unknown, fallback: () => T): T {
            if (!isRecoverableFirestoreReadError(error)) {
                throw error
            }

            console.warn(`[Firebase] Recoverable Firestore read failure in ${operation}; returning fallback.`, error)
            return fallback()
        }
        /**
         * Type-safe collections object with proper Firebase collection references
         */
        type FirebaseCollections = {
            jobRequests: CollectionReference
            jobs: CollectionReference
            mentorings: CollectionReference
            notifications: CollectionReference
            orgs: CollectionReference
            orgTransactions: CollectionReference
            communities: CollectionReference
            communityConnectionRequests: CollectionReference
            profiles: CollectionReference
            projects: CollectionReference
            skills: CollectionReference
            users: CollectionReference
            talentBaseRequests: CollectionReference
            participationRequests: CollectionReference
            blogs: CollectionReference
            intros: CollectionReference
            settings: CollectionReference
            chats: CollectionReference
            chatMessages: (chatId: string) => CollectionReference
            chatrooms: CollectionReference
            presence: CollectionReference
            paidServiceRequests: CollectionReference
            agreements: CollectionReference
            consents: CollectionReference
            membershipRequests: CollectionReference
            collections: CollectionReference
            selfKnowledgeSurveys: CollectionReference
            feedbacks: CollectionReference
            manualMatches: CollectionReference
        }
        function createCollections(db: Firestore): FirebaseCollections {
            return {
                jobRequests: collection(db, 'job-requests'),
                jobs: collection(db, 'jobs'),
                mentorings: collection(db, 'mentorings'),
                notifications: collection(db, 'notifications'),
                orgs: collection(db, 'orgs'),
                orgTransactions: collection(db, 'org-transactions'),
                communities: collection(db, 'communities'),
                communityConnectionRequests: collection(db, Collections.COMMUNITY_CONNECTION_REQUESTS),
                profiles: collection(db, 'profiles'),
                projects: collection(db, 'projects'),
                skills: collection(db, 'skills'),
                users: collection(db, 'users'),
                talentBaseRequests: collection(db, Collections.TALENTBASE_REQUESTS),
                participationRequests: collection(db, 'participation-requests'),
                blogs: collection(db, 'blogs'),
                intros: collection(db, 'intros'),
                settings: collection(db, 'settings'),
                chats: collection(db, 'chats'),
                chatMessages: (chatId: string) => collection(db, `chats/${chatId}/messages`),
                chatrooms: collection(db, 'chatrooms'),
                presence: collection(db, 'presence'),
                paidServiceRequests: collection(db, 'paid-service-requests'),
                agreements: collection(db, 'agreements'),
                consents: collection(db, 'consents'),
                membershipRequests: collection(db, 'membership-requests'),
                collections: collection(db, 'collections'),
                selfKnowledgeSurveys: collection(db, 'self-knowledge-surveys'),
                feedbacks: collection(db, 'feedbacks'),
                manualMatches: collection(db, Collections.MANUAL_MATCHES)
            }
        }

        function getCurrentUserId() {
            return auth.currentUser?.uid
        }

        function getServerTimestamp() {
            return serverTimestamp()
        }

        function getCollection(path: string) {
            return collection(requireDb(), path)
        }

        function resolveCollection(collection: FirestoreCollectionInput): CollectionReference {
            return typeof collection === 'string' ? getCollection(collection) : collection
        }

        function describeFirestoreReadSource(collection: FirestoreReadInput) {
            return collection instanceof Query ? 'query' : resolveCollection(collection).path
        }

        function buildFirestoreReadQuery(
            collection: FirestoreReadInput,
            queryBuilder?: (query: CollectionReference) => Query
        ): Query | CollectionReference {
            if (collection instanceof Query) {
                return collection
            }

            const collectionRef = resolveCollection(collection)
            return queryBuilder ? queryBuilder(collectionRef) : collectionRef
        }

        async function getFirstFirestoreReadDocument(queryToUse: Query | CollectionReference) {
            if (queryToUse instanceof Query) {
                const snapshot = await getDocs(query(queryToUse, limit(1)))
                return snapshot.docs[0]
            }

            const snapshot = await getDocs(query(queryToUse, limit(1)))
            return snapshot.docs[0]
        }

        function serializeDocument<T extends SerializedDocument & any = SerializedDocument>(
            doc: DocumentSnapshot<any, any> | QueryDocumentSnapshot<any, any>
        ): T | null {
            return doc?.exists() ? ({ id: doc.id, ...doc.data() } as any as T) : null
        }

        async function loadFirstDocument<T extends SerializedDocument & any = SerializedDocument>(
            collection: string | CollectionReference | Query,
            queryBuilder?: (query: CollectionReference) => Query
        ): Promise<T> {
            await ensureFirestore()
            const queryToUse = buildFirestoreReadQuery(collection, queryBuilder)

            return withRecoverableFirestoreRead(
                `loadFirstDocument(${describeFirestoreReadSource(collection)})`,
                async () => {
                    const firstDocument = await getFirstFirestoreReadDocument(queryToUse)
                    return firstDocument ? serializeDocument<T>(firstDocument)! : emptyDocument<T>()
                },
                () => emptyDocument<T>()
            )
        }

        async function loadFirstDocumentByAttr<T>(
            collection: string | CollectionReference,
            attribute: string,
            value: any
        ): Promise<T> {
            await ensureFirestore()
            return loadFirstDocument(query(resolveCollection(collection), where(attribute, '==', value)))
        }

        async function loadDocumentByAttrOrId<T extends SerializedDocument & any = SerializedDocument>(
            collection: string | CollectionReference,
            attribute: keyof T,
            value: any
        ): Promise<T> {
            await ensureFirestore()
            const collectionRef = resolveCollection(collection)
            const snapshot = await getDocs(query(collectionRef, where(attribute as unknown as string, '==', value)))
            const id = snapshot.docs[0]?.id || value

            return loadDocumentOrFail(collectionRef, id) as Promise<T>
        }

        async function loadDocument<T extends SerializedDocument & any = SerializedDocument>(
            collection: string | CollectionReference,
            id: string
        ): Promise<T> {
            await ensureFirestore()
            const collectionRef = resolveCollection(collection)
            const docRef = doc(collectionRef, id)

            return withRecoverableFirestoreRead(
                `loadDocument(${collectionRef.path}/${id})`,
                async () => {
                    const docSnapshot = await getDoc(docRef)
                    return serializeDocument<T>(docSnapshot) ?? emptyDocument<T>()
                },
                () => emptyDocument<T>()
            )
        }

        async function loadDocumentOrFail<T extends SerializedDocument>(
            collection: CollectionReference,
            id: string
        ): Promise<T> {
            const data = await loadDocument<T>(collection, id)
            if (!data) {
                throw new DocumentNotFound(collection, id)
            }

            return data
        }

        async function updateDocument<T extends SerializedDocument & any = SerializedDocument>(
            collection: string | CollectionReference,
            id: string,
            data: Partial<T>,
            options: Options & { useSet?: boolean } = {}
        ): Promise<void> {
            await ensureFirestore()
            const { id: _id, ...restOfData } = data as any

            if (options.withAuthor) {
                restOfData[ATTR_UPDATED_BY_ID] = getCurrentUserId()
            }

            if (options.withTimestamps) {
                restOfData[ATTR_UPDATED_AT] = getServerTimestamp()
            }

            const collectionRef = resolveCollection(collection)
            const docRef = typeof collection === 'string' ? doc(requireDb(), collection, id) : doc(collectionRef, id)
            const safeData = sanitizeForFirestore(restOfData)

            await (options.useSet ? setDoc(docRef, safeData) : updateDoc(docRef, safeData))
        }

        async function listDocuments<T extends SerializedDocument & any = SerializedDocument>(
            collection: string | CollectionReference | Query,
            queryBuilder?: (query: CollectionReference) => Query
        ): Promise<T[]> {
            await ensureFirestore()
            const queryToUse = buildFirestoreReadQuery(collection, queryBuilder)

            return withRecoverableFirestoreRead(
                `listDocuments(${describeFirestoreReadSource(collection)})`,
                async () => {
                    const snapshot = await getDocs(queryToUse)
                    return snapshot.docs.map((doc) => serializeDocument<T>(doc)).filter(Boolean) as T[]
                },
                () => []
            )
        }

        async function createDocument<T extends SerializedDocument & any = SerializedDocument>(
            collection: string | CollectionReference,
            data: Partial<T>,
            options: Options = {}
        ): Promise<DocumentReference> {
            await ensureFirestore()
            const { id: _id, ...restOfData } = data as any

            if (options.withAuthor) {
                restOfData[ATTR_CREATED_BY_ID] = getCurrentUserId()
                restOfData[ATTR_UPDATED_BY_ID] = getCurrentUserId()
            }

            if (options.withTimestamps) {
                restOfData[ATTR_CREATED_AT] = getServerTimestamp()
                restOfData[ATTR_UPDATED_AT] = getServerTimestamp()
            }

            if (options.withSoftDelete) {
                restOfData[ATTR_DELETED_AT] = null
            }

            return await addDoc(resolveCollection(collection), sanitizeForFirestore(restOfData))
        }

        function suspenseDocumentRef(ref: any) {
            return ref instanceof DocumentReference || typeof ref === 'string' ? undefined : ref || undefined
        }

        function getCollectionByChatTopicBase(topicBase: string): string {
            switch (topicBase) {
                case 'participationRequest':
                    return Collections.PARTICIPATION_REQUESTS
                case 'project':
                    return Collections.PROJECTS
                case 'skill':
                    return Collections.SKILLS
                case 'mentoring':
                    return Collections.MENTORINGS
            }

            return ''
        }

        // Return all functions and objects for use in the app
        return {
            provide: {
                // Provide auth directly - this is the new pattern
                auth,
                // Firebase object for firestore, storage, etc (no auth here)
                firebase: {
                    app,
                    // Getters: they throw until ensureFirestore() has resolved.
                    get db() {
                        return requireDb()
                    },
                    fn,
                    storage,
                    get collections() {
                        return requireCollections()
                    },
                    ensureFirestore,
                    getCurrentUserId,
                    getServerTimestamp,
                    getCollection,
                    resolveCollection,
                    serializeDocument,
                    loadFirstDocument,
                    loadFirstDocumentByAttr,
                    loadDocumentByAttrOrId,
                    loadDocument,
                    loadDocumentOrFail,
                    listDocuments,
                    createDocument,
                    updateDocument,
                    suspenseDocumentRef,
                    getCollectionByChatTopicBase
                }
            }
        }
    }
})
